import React, { useState, useEffect, useRef, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea, ReferenceDot, ReferenceLine } from 'recharts';
import { Thermometer } from 'lucide-react';

/**
 * Shared Blood Trend Chart Component
 * 
 * For My Progress page: Uses TIME-BASED X-axis with unified domain
 * - X-axis spans from earliest date to TODAY
 * - Line stops at last result, but axis continues to today
 * - This allows treatment timeline below to align perfectly
 * 
 * CRITICAL: All chart rendering configuration is locked here.
 * NO per-chart overrides allowed.
 */

// LOCKED CONFIGURATION - DO NOT OVERRIDE
const CHART_CONFIG = {
  // Fixed heights (no responsive, no vh, no percentage)
  height: {
    mobile: 180,  // Significantly reduced for mobile - much shorter charts
    desktop: 320
  },
  
  // Margins - tighter on mobile to maximize chart width
  margin: {
    mobile: {
      top: 2,
      bottom: 2,
      left: 8,
      right: 20
    },
    desktop: {
      top: 2,
      bottom: 2,
      left: 20,
      right: 35
    }
  },
  
  // Font sizes
  fontSize: {
    mobile: 10,
    desktop: 12
  },
  
  // Line styling
  line: {
    strokeWidth: {
      mobile: 2.5,
      desktop: 3
    },
    dotRadius: {
      mobile: 4,
      desktop: 5
    },
    activeDotRadius: {
      mobile: 6,
      desktop: 7
    }
  },
  
  // Reference area opacity
  referenceOpacity: {
    mobile: 0.08,
    desktop: 0.1
  }
};

const BloodTrendChart = ({ 
  data, 
  markerConfig, 
  showLegend = true,
  onDataReady,
  pageDomain = null,
  selectedTreatment = null  // Treatment to show overlay markers for
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tooltipActive, setTooltipActive] = useState(false);
  const tooltipOpenedAt = useRef(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get configuration based on device
  const height = isMobile ? CHART_CONFIG.height.mobile : CHART_CONFIG.height.desktop;
  const fontSize = isMobile ? CHART_CONFIG.fontSize.mobile : CHART_CONFIG.fontSize.desktop;
  const strokeWidth = isMobile ? CHART_CONFIG.line.strokeWidth.mobile : CHART_CONFIG.line.strokeWidth.desktop;
  const dotRadius = isMobile ? CHART_CONFIG.line.dotRadius.mobile : CHART_CONFIG.line.dotRadius.desktop;
  const activeDotRadius = isMobile ? CHART_CONFIG.line.activeDotRadius.mobile : CHART_CONFIG.line.activeDotRadius.desktop;
  const refOpacity = isMobile ? CHART_CONFIG.referenceOpacity.mobile : CHART_CONFIG.referenceOpacity.desktop;

  const hasData = data && data.length > 0;

  // Calculate Y-axis domain (locked, no auto-scaling)
  const yDomain = [0, markerConfig.range.max * 1.2];
  
  // Fixed tick count
  const tickCount = 5;
  
  // Y-axis width - tighter on mobile to maximize chart width
  const yAxisWidth = isMobile ? 28 : 40;
  
  // Transform data to use timestamps for time-based X-axis when pageDomain is provided
  const chartData = useMemo(() => {
    if (!hasData) return [];
    
    if (pageDomain) {
      // Use timestamp-based data for unified timeline
      return data.map(d => ({
        ...d,
        timestamp: d.timestamp || new Date(d.fullDate || d.date).getTime()
      }));
    }
    
    // Fallback to category-based (for dashboard charts)
    return data;
  }, [data, hasData, pageDomain]);

  // X-axis configuration - TIME-BASED when pageDomain provided
  const xAxisConfig = useMemo(() => {
    if (pageDomain) {
      // Time-based X-axis for My Progress page
      // Domain is FIXED based on time window selection
      const startYear = new Date(pageDomain.domainStartTs).getFullYear();
      const endYear = new Date(pageDomain.domainEndTs).getFullYear();
      const startMonth = new Date(pageDomain.domainStartTs).getMonth();
      const endMonth = new Date(pageDomain.domainEndTs).getMonth();
      const spansMultipleYears = startYear !== endYear;
      
      // Format function for labels - always show month and year for clarity
      const formatTick = (ts) => {
        const date = new Date(ts);
        const month = date.toLocaleDateString('en-AU', { month: 'short' });
        const year = date.getFullYear().toString().slice(-2);
        return `${month} '${year}`;
      };
      
      // Generate ticks that clearly show the time window
      const generateWindowTicks = () => {
        const start = pageDomain.domainStartTs;
        const end = pageDomain.domainEndTs;
        const totalMs = end - start;
        const totalMonths = Math.round(totalMs / (1000 * 60 * 60 * 24 * 30));
        
        const ticks = [];
        const startDate = new Date(start);
        const endDate = new Date(end);
        const now = new Date();
        
        // Check if this is a "live" view (domain ends within 7 days of today)
        const isLiveView = Math.abs(end - now.getTime()) < (7 * 24 * 60 * 60 * 1000);
        const currentMonthTick = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
        
        if (totalMonths <= 12) {
          // 12 months: quarterly ticks (every 3 months)
          for (let i = 0; i <= 4; i++) {
            const tickDate = new Date(startDate.getFullYear(), startDate.getMonth() + (i * 3), 1);
            if (tickDate.getTime() <= end) {
              ticks.push(tickDate.getTime());
            }
          }
          // Add current month for live views, or last month for data views
          const lastTick = isLiveView ? currentMonthTick : new Date(endDate.getFullYear(), endDate.getMonth(), 1).getTime();
          if (!ticks.includes(lastTick)) {
            ticks.push(lastTick);
          }
        } else if (totalMonths <= 24) {
          // 24 months: every 6 months
          for (let i = 0; i <= 4; i++) {
            const tickDate = new Date(startDate.getFullYear(), startDate.getMonth() + (i * 6), 1);
            if (tickDate.getTime() <= end) {
              ticks.push(tickDate.getTime());
            }
          }
          // Add current month for live views, or last month for data views
          const lastTick = isLiveView ? currentMonthTick : new Date(endDate.getFullYear(), endDate.getMonth(), 1).getTime();
          if (!ticks.includes(lastTick)) {
            ticks.push(lastTick);
          }
        } else if (totalMonths <= 38) {
          // 36 months: every 6 months (same pattern as 24M but covering 3 years)
          // Use <= 38 to account for rounding differences in month calculation
          // Generate ticks at 6-month intervals from start date
          for (let i = 0; i <= 7; i++) {
            const tickDate = new Date(startDate.getFullYear(), startDate.getMonth() + (i * 6), 1);
            const tickTs = tickDate.getTime();
            if (tickTs >= start && tickTs <= end) {
              ticks.push(tickTs);
            }
          }
          // Add current month for live views, or last month for data views
          const lastTick = isLiveView ? currentMonthTick : new Date(endDate.getFullYear(), endDate.getMonth(), 1).getTime();
          if (!ticks.includes(lastTick)) {
            ticks.push(lastTick);
          }
        } else {
          // "All" view or longer: January of each year, but skip if too close to current month
          for (let year = startDate.getFullYear(); year <= endDate.getFullYear() + 1; year++) {
            const janFirst = new Date(year, 0, 1).getTime();
            if (janFirst >= start && janFirst <= end) {
              // For live views, skip January if it's within 5 months of current month
              if (isLiveView) {
                const monthsFromCurrent = (currentMonthTick - janFirst) / (1000 * 60 * 60 * 24 * 30);
                if (monthsFromCurrent < 5 && monthsFromCurrent > 0) {
                  continue; // Skip this January - too close to current month
                }
              }
              ticks.push(janFirst);
            }
          }
          // Always add current month for live views
          if (isLiveView && !ticks.includes(currentMonthTick)) {
            ticks.push(currentMonthTick);
          } else if (!isLiveView) {
            // For data views, add last month only if far enough from nearest tick
            const lastTick = new Date(endDate.getFullYear(), endDate.getMonth(), 1).getTime();
            const nearestTick = ticks.length > 0 ? ticks[ticks.length - 1] : 0;
            const monthsFromNearest = (lastTick - nearestTick) / (1000 * 60 * 60 * 24 * 30);
            if (!ticks.includes(lastTick) && monthsFromNearest > 3) {
              ticks.push(lastTick);
            }
          }
        }
        
        // Sort ticks to ensure proper order
        ticks.sort((a, b) => a - b);
        
        return ticks;
      };
      
      const displayTicks = generateWindowTicks();
      
      // Create tick data with labels for the timeline to use
      const ticksWithLabels = displayTicks.map(ts => ({
        timestamp: ts,
        label: formatTick(ts),
        position: (ts - pageDomain.domainStartTs) / (pageDomain.domainEndTs - pageDomain.domainStartTs)
      }));
      
      return {
        dataKey: 'timestamp',
        domain: [pageDomain.domainStartTs, pageDomain.domainEndTs],
        type: 'number',
        scale: 'time',
        tickFormatter: formatTick,
        ticks: displayTicks,
        ticksWithLabels: ticksWithLabels
      };
    }
    
    // Default category-based for dashboard
    return {
      dataKey: 'date',
      domain: ['auto', 'auto'],
      type: 'category',
      scale: 'auto',
      tickFormatter: undefined,
      ticks: undefined,
      ticksWithLabels: []
    };
  }, [pageDomain]);

  // Notify parent of axis configuration for timeline alignment
  useEffect(() => {
    if (onDataReady && pageDomain) {
      onDataReady({
        domainStartTs: pageDomain.domainStartTs,
        domainEndTs: pageDomain.domainEndTs,
        ticks: xAxisConfig.ticksWithLabels || [],
        margin: { left: yAxisWidth, right: 0 }
      });
    }
  }, [onDataReady, pageDomain, xAxisConfig.ticksWithLabels, yAxisWidth]);

  // Mobile tooltip control handlers
  const handleChartClick = () => {
    if (isMobile) {
      // Only close if tooltip has been open for at least 300ms
      // This prevents the tap that opens it from also closing it
      const now = Date.now();
      if (tooltipActive && (now - tooltipOpenedAt.current > 300)) {
        setTooltipActive(false);
      }
    }
  };

  const handleMouseMove = () => {
    if (isMobile && !tooltipActive) {
      // On mobile, touching a data point activates tooltip
      tooltipOpenedAt.current = Date.now();
      setTooltipActive(true);
    }
  };

  // Custom tooltip with X close button for mobile
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    // Format the label - if it's a timestamp (number), convert to date
    // timeZone UTC: data timestamps are parsed from YYYY-MM-DD as UTC midnight,
    // so formatting in UTC always shows the stored date (no off-by-one in US timezones)
    const formattedLabel = typeof label === 'number' 
      ? new Date(label).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
      : label;
    
    // Check if this is an illness reading
    const isIllness = payload[0]?.payload?.isDuringIllness;
    
    return (
      <div style={{
        backgroundColor: '#ffffff',
        border: isIllness ? '2px solid #f59e0b' : '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: `${fontSize}px`,
        padding: isMobile ? '10px 14px' : '8px 12px',
        position: 'relative',
        minWidth: isMobile ? '120px' : 'auto'
      }}>
        {isMobile && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setTooltipActive(false);
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              setTooltipActive(false);
            }}
            style={{
              position: 'absolute',
              top: '4px',
              right: '6px',
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              fontSize: '12px',
              cursor: 'pointer',
              color: '#475569',
              padding: '0',
              lineHeight: '20px',
              textAlign: 'center',
              fontWeight: 'bold'
            }}
          >
            ✕
          </button>
        )}
        <p style={{ fontWeight: 'bold', color: '#1e293b', margin: 0, paddingRight: isMobile ? '20px' : '0' }}>
          {formattedLabel}
        </p>
        <p style={{ color: markerConfig.color, margin: '4px 0 0 0' }}>
          {markerConfig.label}: {payload[0].value} {markerConfig.unit}
        </p>
        {isIllness && (
          <p style={{ color: '#f59e0b', margin: '4px 0 0 0', fontSize: fontSize - 1 }}>
            🌡️ Taken while unwell — counts can be temporarily elevated.
          </p>
        )}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart 
        data={hasData ? chartData : []} 
        margin={isMobile ? CHART_CONFIG.margin.mobile : CHART_CONFIG.margin.desktop}
        onClick={handleChartClick}
        onMouseMove={handleMouseMove}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        
        <XAxis
          dataKey={xAxisConfig.dataKey}
          stroke="#64748b"
          style={{ fontSize: `${fontSize}px` }}
          tick={{ fontSize }}
          domain={xAxisConfig.domain}
          ticks={xAxisConfig.ticks}
          type={xAxisConfig.type}
          scale={xAxisConfig.scale}
          tickFormatter={xAxisConfig.tickFormatter}
          allowDataOverflow={true}
          interval={0}
        />
        
        <YAxis
          stroke="#64748b"
          style={{ fontSize: `${fontSize}px` }}
          domain={yDomain}
          width={yAxisWidth}
          tickCount={tickCount}
          allowDataOverflow={false}
          scale="linear"
          type="number"
        />
        
        {/* Reference Range Area */}
        <ReferenceArea
          y1={markerConfig.range.min}
          y2={markerConfig.range.max}
          fill={markerConfig.color}
          fillOpacity={refOpacity}
          ifOverflow="hidden"
        />
        
        {hasData && (
          <>
            <Tooltip
              active={isMobile ? tooltipActive : undefined}
              content={<CustomTooltip />}
            />
            
            <Line
              type="monotone"
              dataKey="value"
              name={markerConfig.label}
              stroke={markerConfig.color}
              strokeWidth={strokeWidth}
              dot={(props) => {
                const { cx, cy, payload } = props;
                const isIllness = payload?.isDuringIllness;
                
                if (isIllness) {
                  // Hollow circle for illness readings
                  return (
                    <circle
                      key={`dot-${cx}-${cy}`}
                      cx={cx}
                      cy={cy}
                      r={dotRadius}
                      fill="white"
                      stroke={markerConfig.color}
                      strokeWidth={2}
                    />
                  );
                }
                
                // Normal filled dot
                return (
                  <circle
                    key={`dot-${cx}-${cy}`}
                    cx={cx}
                    cy={cy}
                    r={dotRadius}
                    fill={markerConfig.color}
                  />
                );
              }}
              activeDot={{ r: activeDotRadius }}
              isAnimationActive={false}
              connectNulls={false}
            />
            
            {/* Treatment overlay - start line */}
            {selectedTreatment && pageDomain && (() => {
              const startTs = new Date(selectedTreatment.startDate || selectedTreatment.start_date).getTime();
              const endTs = selectedTreatment.endDate || selectedTreatment.end_date 
                ? new Date(selectedTreatment.endDate || selectedTreatment.end_date).getTime() 
                : null;
              
              // Get category color
              const categoryColors = {
                vitamin: '#eab308',
                supplement: '#22c55e',
                medication: '#3b82f6',
                other: '#a855f7'
              };
              const category = (selectedTreatment.category || 'other').toLowerCase();
              const color = categoryColors[category] || categoryColors.other;
              
              // Only show if within domain
              const domainStart = pageDomain.domainStartTs;
              const domainEnd = pageDomain.domainEndTs;
              
              const startInView = startTs >= domainStart && startTs <= domainEnd;
              const endInView = endTs && endTs >= domainStart && endTs <= domainEnd;
              
              return (
                <>
                  {/* Treatment period tint (if has end date) */}
                  {endTs && (
                    <ReferenceArea
                      x1={Math.max(startTs, domainStart)}
                      x2={Math.min(endTs, domainEnd)}
                      fill={color}
                      fillOpacity={0.08}
                      ifOverflow="hidden"
                    />
                  )}
                  
                  {/* Start line */}
                  {startInView && (
                    <ReferenceLine
                      x={startTs}
                      stroke={color}
                      strokeWidth={2}
                      strokeDasharray="5 3"
                      label={{
                        value: selectedTreatment.name?.substring(0, 15) || 'Start',
                        position: 'top',
                        fill: color,
                        fontSize: isMobile ? 9 : 10
                      }}
                    />
                  )}
                  
                  {/* End line (if has end date) */}
                  {endInView && (
                    <ReferenceLine
                      x={endTs}
                      stroke={color}
                      strokeWidth={2}
                      strokeDasharray="5 3"
                      label={{
                        value: 'End',
                        position: 'top',
                        fill: color,
                        fontSize: isMobile ? 9 : 10
                      }}
                    />
                  )}
                </>
              );
            })()}
            
            {/* "Last result" label under the final data point - only for live views */}
            {chartData.length > 0 && pageDomain && (() => {
              const now = new Date();
              const isLiveView = pageDomain.domainEndTs && 
                Math.abs(pageDomain.domainEndTs - now.getTime()) < (7 * 24 * 60 * 60 * 1000);
              
              if (!isLiveView) return null;
              
              // Find data points within the visible domain
              const visibleData = chartData.filter(d => 
                d.timestamp >= pageDomain.domainStartTs && d.timestamp <= pageDomain.domainEndTs
              );
              
              if (visibleData.length === 0) return null;
              
              const lastPoint = visibleData[visibleData.length - 1];
              
              if (!lastPoint) return null;
              
              return (
                <ReferenceDot
                  x={lastPoint.timestamp}
                  y={lastPoint.value}
                  r={0}
                  label={{
                    value: 'Last result',
                    position: 'bottom',
                    fill: '#64748b',
                    fontSize: isMobile ? 9 : 10,
                    dy: 8
                  }}
                />
              );
            })()}
          </>
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default BloodTrendChart;
